import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TbChevronDown, TbLock, TbSearch } from "react-icons/tb";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../../Hooks/usePortalDropdownPosition";

const TRIGGER_CLASS =
  "valut-input flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-lg !pr-10 text-left text-sm outline-none focus-visible:border-[#e7a73f] focus-visible:ring-2 focus-visible:ring-[#e7a73f]/20 disabled:cursor-not-allowed disabled:opacity-60";

const SEARCH_THRESHOLD = 5;

/**
 * Searchable single-select combobox for Baas console (long instance / entity lists).
 * Uses a portal so the menu escapes MUI Dialog overflow/clipping.
 * Pass `locked` to keep the same trigger layout but force read-only (lock icon, no menu).
 */
export const BaasSearchableSelect = ({
  id,
  label,
  value,
  onChange,
  options = [],
  placeholder = "",
  searchPlaceholder = "",
  emptyMessage = "",
  disabled = false,
  locked = false,
  getOptionLabel,
  getOptionSearchText,
  getOptionSecondary,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isDisabled = disabled || locked;
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open && !isDisabled);

  const selected = useMemo(
    () => options.find((option) => String(option.id) === String(value)) ?? null,
    [options, value],
  );

  const resolveLabel = (option) => {
    if (!option) return "";
    if (getOptionLabel) return getOptionLabel(option);
    return option.label || option.name || String(option.id);
  };

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const label = getOptionLabel
        ? getOptionLabel(option)
        : option.label || option.name || String(option.id);
      const haystack = getOptionSearchText
        ? getOptionSearchText(option)
        : [label, option.id, option.environment, option.type]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
      return String(haystack).toLowerCase().includes(term);
    });
  }, [options, search, getOptionSearchText, getOptionLabel]);

  const showSearch = options.length >= SEARCH_THRESHOLD;

  const handleSelect = (optionId) => {
    onChange(String(optionId));
    setOpen(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
        >
          {label}
        </label>
      ) : null}

      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open && !isDisabled}
          aria-disabled={isDisabled}
          aria-readonly={locked || undefined}
          disabled={isDisabled}
          onClick={() => !isDisabled && setOpen((prev) => !prev)}
          className={`${TRIGGER_CLASS} ${
            selected && getOptionSecondary ? "!h-auto min-h-10 py-1.5" : ""
          } ${selected ? "text-brand-dark dark:text-gray-100" : "text-gray-400"} ${
            locked ? "!opacity-100 bg-gray-50 dark:bg-brand-dark-elevated/40" : ""
          }`}
        >
          <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            <span className="w-full truncate font-medium">
              {selected ? resolveLabel(selected) : placeholder}
            </span>
            {selected && getOptionSecondary ? (
              <span className="w-full truncate text-[11px] font-normal text-gray-400">
                {getOptionSecondary(selected)}
              </span>
            ) : null}
          </span>
        </button>
        {locked ? (
          <TbLock
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
        ) : (
          <TbChevronDown
            className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        )}

        {open &&
          !isDisabled &&
          menuStyle &&
          createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={handleClose}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="listbox"
                style={{
                  ...menuStyle,
                  zIndex: PORTAL_DROPDOWN_Z_INDEX,
                  display: "flex",
                  flexDirection: "column",
                }}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:shadow-none"
              >
                {showSearch ? (
                  <div className="shrink-0 border-b border-gray-100 p-2 dark:border-brand-dark-border">
                    <div className="relative">
                      <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={searchPlaceholder}
                        className="valut-input h-9 w-full rounded-lg pl-9 pr-3 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-y-auto py-1">
                  {filteredOptions.length === 0 ? (
                    <p className="px-3 py-4 text-center text-xs text-gray-400">{emptyMessage}</p>
                  ) : (
                    filteredOptions.map((option) => {
                      const optionId = String(option.id);
                      const isActive = optionId === String(value);
                      return (
                        <button
                          key={optionId}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => handleSelect(optionId)}
                          className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm transition-colors ${
                            isActive
                              ? "bg-[#fff8ec] text-brand-dark dark:bg-brand-dark-surface dark:text-gray-100"
                              : "text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#1f1f1f]"
                          }`}
                        >
                          <span className="w-full truncate font-medium">{resolveLabel(option)}</span>
                          {getOptionSecondary ? (
                            <span className="w-full truncate text-[11px] text-gray-400">
                              {getOptionSecondary(option)}
                            </span>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
      </div>
    </div>
  );
};
